import React, { useState, useEffect } from 'react';
import QrCode from '../components/QrCode';
import { getMyPunchQr, setPunchQrDisplay } from '../services/kioskService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService } from '../services/attendanceService';
import { companiesService, type CompanySettings } from '../services/companiesService';
import { leaveBalanceService } from '../services/leaveBalanceService';
import InstallPrompt from '../components/InstallPrompt';
import api from '../services/api';
import type { AttendanceRecord, MonthlyStats, TeamMemberStatus } from '../types/attendance';
import type { LeaveBalance } from '../types/leaveBalance';
import Pagination from '../components/Pagination';

interface UserPermissions {
  canApproveCorrections: boolean;
  canExportAttendance: boolean;
  attendanceViewScope: 'self' | 'department' | 'company' | 'custom';
  viewableUserIds?: number[];
}

/**
 * 打刻用QRのパネル。**本人が表示/非表示を選ぶ。**
 *
 * 共用端末で毎日打刻する人は、ダッシュボードに出しっぱなしの方が速い。
 * 一方で共用端末を使わない人には邪魔なだけなので、会社ではなく本人が決める。
 * 設定は端末ではなくサーバに持つ（スマホを変えても引き継がれる）。
 */
const PunchQrPanel: React.FC = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getMyPunchQr()
      .then((r) => {
        setToken(r.qrToken);
        setShow(r.showOnDashboard);
      })
      .catch(() => { /* 取得できなくてもダッシュボードは壊さない */ });
  }, []);

  const toggle = async () => {
    const next = !show;
    setShow(next); // 先に反映して待たせない
    setLoading(true);
    try {
      await setPunchQrDisplay(next);
    } catch {
      setShow(!next); // 失敗したら戻す
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      {show && token && (
        <div style={{
          display: 'inline-block',
          padding: '16px',
          backgroundColor: 'white',
          border: '1px solid #d0d7de',
          borderRadius: '8px',
          marginBottom: '10px',
        }}>
          <QrCode value={token} size={180} title="自分の打刻用QRコード" />
          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
            共用端末のカメラにかざしてください
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={toggle}
          disabled={loading}
          style={{
            background: 'none',
            border: 'none',
            color: '#0969da',
            fontSize: '13px',
            cursor: loading ? 'wait' : 'pointer',
            textDecoration: 'underline',
            padding: '4px 8px',
          }}
        >
          {show ? 'QRコードを非表示にする' : 'ダッシュボードにQRコードを表示する'}
        </button>
        <button
          onClick={() => navigate('/my-punch-qr')}
          style={{
            background: 'none',
            border: 'none',
            color: '#6c757d',
            fontSize: '13px',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: '4px 8px',
          }}
        >
          大きく表示
        </button>
      </div>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [todayAttendance, setTodayAttendance] = useState<AttendanceRecord | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [teamStatus, setTeamStatus] = useState<TeamMemberStatus[]>([]);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  // ページネーション状態（チーム出勤状況用）
  const [teamStatusPage, setTeamStatusPage] = useState(1);
  const teamStatusItemsPerPage = 20;

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  // 時計を1秒ごとに更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      // キャッシュバスター: タイムスタンプを使って毎回新しいリクエストを強制
      const cacheBuster = Date.now();

      // 現在の勤怠記録取得（未退勤の記録または今日の記録）
      const attendance = await attendanceService.getCurrentAttendance(user.id);
      setTodayAttendance(attendance);

      // 月次統計取得
      const stats = await attendanceService.getMonthlyStats();
      setMonthlyStats(stats);

      // 権限情報取得
      const permsResponse = await api.get<UserPermissions>(`/auth/permissions?_t=${cacheBuster}`);
      setPermissions(permsResponse.data);

      // 企業設定取得（全ユーザー）
      try {
        const settings = await companiesService.getSettings();
        setCompanySettings(settings);
      } catch (error) {
        console.error('企業設定取得エラー:', error);
      }

      // チーム状況取得（閲覧範囲が自分のみ以外の場合）
      if (permsResponse.data.attendanceViewScope !== 'self') {
        const team = await attendanceService.getTeamStatus();
        setTeamStatus(team);
      }

      // 休暇残数取得
      try {
        const balances = await leaveBalanceService.getUserBalances(user.id);
        setLeaveBalances(balances);
      } catch (error) {
        console.error('休暇残数取得エラー:', error);
      }
    } catch (error) {
      console.error('ダッシュボードデータ取得エラー:', error);
    }
  };

  const handleClockIn = async () => {
    if (!user) return;

    setIsLoading(true);
    setMessage('');

    try {
      await attendanceService.clockIn();
      setMessage('出勤打刻が完了しました');
      await loadDashboardData();
    } catch (error: any) {
      setMessage(error.response?.data?.error || '出勤打刻に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOut = async () => {
    // 今日の勤怠記録のIDを取得（todayAttendanceを使用）
    if (!todayAttendance?.id) return;

    setIsLoading(true);
    setMessage('');

    try {
      await attendanceService.clockOut(todayAttendance.id);
      setMessage('退勤打刻が完了しました');
      await loadDashboardData();
    } catch (error: any) {
      setMessage(error.response?.data?.error || '退勤打刻に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBreakStart = async () => {
    // 今日の勤怠記録のIDを取得（todayAttendanceを使用）
    if (!todayAttendance?.id) return;

    setIsLoading(true);
    setMessage('');

    try {
      await attendanceService.startBreak(todayAttendance.id);
      setMessage('休憩開始が完了しました');
      await loadDashboardData();
    } catch (error: any) {
      setMessage(error.response?.data?.error || '休憩開始に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    // 今日の勤怠記録のIDを取得（todayAttendanceを使用）
    if (!todayAttendance?.id) return;

    setIsLoading(true);
    setMessage('');

    try {
      await attendanceService.endBreak(todayAttendance.id);
      setMessage('休憩終了が完了しました');
      await loadDashboardData();
    } catch (error: any) {
      setMessage(error.response?.data?.error || '休憩終了に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timeString: string | null, referenceDate?: string | null) => {
    if (!timeString) return '---';

    const date = new Date(timeString);
    const today = new Date().toISOString().split('T')[0];
    const timeDate = timeString.split('T')[0];

    // 日跨ぎチェック: 今日と異なる日付 or 参照日と異なる日付
    const isDifferentDay = timeDate !== today || (referenceDate && timeString.split('T')[0] !== referenceDate.split('T')[0]);

    if (isDifferentDay) {
      // 日跨ぎの場合: 日付 + 時刻
      return date.toLocaleString('ja-JP', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      // 同じ日の場合: 時刻のみ
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getRoleName = (role: string) => {
    const roleNames: { [key: string]: string } = {
      master: 'マスター',
      admin: '管理者',
      manager: 'マネージャー',
      employee: '従業員'
    };
    return roleNames[role] || role;
  };

  if (!user) return <div>読み込み中...</div>;

  const isMaster = user.role === 'master';
  const canApprove = permissions?.canApproveCorrections;
  const showTeamStatus = permissions && permissions.attendanceViewScope !== 'self';

  // デバッグ用ログ
  console.log('Debug - user.role:', user.role);
  console.log('Debug - isMaster:', isMaster);
  console.log('Debug - permissions:', permissions);
  console.log('Debug - canApprove:', canApprove);

  // 今日の日付チェック: 勤怠記録が今日のものかどうかを判定（JST基準）
  const getJSTDate = (date: Date = new Date()): string => {
    const jstDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
    const year = jstDate.getFullYear();
    const month = String(jstDate.getMonth() + 1).padStart(2, '0');
    const day = String(jstDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = getJSTDate();

  // 今日の記録かどうかを判定（clock_in または clock_out が今日の日付）
  const isTodayRecord = todayAttendance
    ? (todayAttendance.clock_in?.split('T')[0] === today ||
       todayAttendance.clock_out?.split('T')[0] === today)
    : false;

  // 未退勤の記録は日付に関係なく表示する（前日から持ち越し対応）
  // 今日の記録、または未退勤の記録のみを有効とする
  const isUnfinished = todayAttendance?.clock_in && !todayAttendance?.clock_out;
  const validTodayAttendance = (isTodayRecord || isUnfinished) ? todayAttendance : null;

  // デバッグ用ログ（日付チェック）
  const now = new Date();
  const utcDate = now.toISOString().split('T')[0];
  const jstTime = now.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  console.log('Debug - JST time:', jstTime);
  console.log('Debug - today (JST):', today);
  console.log('Debug - UTC date:', utcDate);
  console.log('Debug - todayAttendance:', todayAttendance);
  console.log('Debug - clock_in date:', todayAttendance?.clock_in?.split('T')[0]);
  console.log('Debug - isTodayRecord:', isTodayRecord);
  console.log('Debug - isUnfinished:', isUnfinished);
  console.log('Debug - validTodayAttendance:', validTodayAttendance);

  // 今日のデータのみなので、clock_inとclock_outの状態で判定
  const attendedCount = teamStatus.filter(m => m.clock_in !== null && m.clock_out === null).length; // 勤務中
  const clockedOutCount = teamStatus.filter(m => m.clock_in !== null && m.clock_out !== null).length; // 退勤済み（今日出勤して退勤した人）
  const notAttendedCount = teamStatus.filter(m => m.clock_in === null).length; // 未出勤（今日まだ打刻していない人）

  // チーム出勤状況のページネーション計算
  const teamStatusTotalPages = Math.ceil(teamStatus.length / teamStatusItemsPerPage);
  const teamStatusStartIndex = (teamStatusPage - 1) * teamStatusItemsPerPage;
  const teamStatusEndIndex = teamStatusStartIndex + teamStatusItemsPerPage;
  const paginatedTeamStatus = teamStatus.slice(teamStatusStartIndex, teamStatusEndIndex);

  return (
    <div style={{ minHeight: '100vh', padding: '20px' }}>
      {/* ヘッダー */}
      <header style={{
        maxWidth: '1200px',
        margin: '0 auto 30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: '1px solid #e1e4e8'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ margin: 0, fontSize: 'clamp(18px, 5vw, 24px)', color: '#24292e', fontWeight: '600' }}>ダッシュボード</h1>
            <p style={{ margin: '6px 0 0', color: '#586069', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              {user.companyName} · {user.name}さん · {getRoleName(user.role)}
            </p>
          </div>
          {/* 常設のインストール導線。バナーで「あとで」を押しても、ここからいつでも追加できる */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <InstallPrompt variant="button" />
          <button
            onClick={logout}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#586069',
              border: '1px solid #d1d5da',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f6f8fa';
              e.currentTarget.style.borderColor = '#c3c8cf';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5da';
            }}
          >
            ログアウト
          </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* ホーム画面への追加導線（打刻ボタンのすぐ上＝毎日必ず見る位置に置く） */}
        <InstallPrompt />

        {/* メッセージ表示 */}
        {message && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '8px',
            backgroundColor: message.includes('失敗') ? '#f8d7da' : '#d4edda',
            color: message.includes('失敗') ? '#721c24' : '#155724',
            border: `1px solid ${message.includes('失敗') ? '#f5c6cb' : '#c3e6cb'}`
          }}>
            {message}
          </div>
        )}

        {/*
          共用端末で打刻する人向け。**本人が出す/出さないを選べる**。
          毎回QR画面へ移動するより出しっぱなしの方が速い。
          **打刻欄より上に置く** — 端末の前でスクロールさせないため。
          共用端末を使わない人には邪魔なだけなので既定は非表示。
        */}
        <PunchQrPanel />

        {/* 今日の勤怠状況 + 打刻ボタン */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#24292e', fontWeight: '600' }}>
            今日の勤怠状況
          </h2>

          {/* 現在時刻 */}
          <div style={{
            textAlign: 'center',
            marginBottom: '28px',
            padding: '28px 20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '2px solid #007bff'
          }}>
            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px', fontWeight: '500' }}>現在時刻</div>
            <div style={{ fontSize: '56px', fontWeight: '300', color: '#007bff', fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '2px' }}>
              {currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '10px' }}>
              {currentTime.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
            </div>
          </div>

          {/* 時刻表示（シンプルなリスト形式） */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: companySettings?.auto_break_deduction_enabled ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '28px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>出勤時刻</div>
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#28a745' }}>
                {formatTime(validTodayAttendance?.clock_in || null)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>退勤時刻</div>
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#dc3545' }}>
                {formatTime(validTodayAttendance?.clock_out || null, validTodayAttendance?.clock_in || null)}
              </div>
            </div>
            {!companySettings?.auto_break_deduction_enabled && (
              <>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>休憩開始</div>
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#495057' }}>
                    {formatTime(validTodayAttendance?.break_start || null)}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>休憩終了</div>
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#495057' }}>
                    {formatTime(validTodayAttendance?.break_end || null)}
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* 退勤済みメッセージ（今日退勤した場合のみ表示） */}
            {validTodayAttendance?.clock_out && (
              <div style={{
                padding: '12px 28px',
                backgroundColor: '#f6f8fa',
                color: '#24292e',
                fontWeight: '500',
                borderRadius: '6px',
                fontSize: '14px',
                border: '1px solid #e1e4e8'
              }}>
                ✓ 本日の勤務が完了しています
              </div>
            )}

            {/*
              会社が「共用端末でのみ打刻を許す」設定にしている場合は、
              ここに打刻ボタンを出さない。**黙って消すと故障に見える**ので理由を書く。
              打刻修正申請は使えるままなので、打ち損ねても回復できる。
            */}
            {companySettings?.self_punch_enabled === false && (
              <div style={{
                padding: '16px 20px',
                backgroundColor: '#f6f8fa',
                border: '1px solid #d0d7de',
                borderRadius: '6px',
                color: '#57606a',
                fontSize: '14px',
                lineHeight: 1.8,
                textAlign: 'center',
                maxWidth: '520px',
              }}>
                <strong>共用端末からのみ打刻できる設定になっています。</strong><br />
                設置された端末でQRコードをかざしてください。
              </div>
            )}

            {/* 出勤ボタン: 未出勤 or 退勤済み（連続勤務対応） */}
            {companySettings?.self_punch_enabled !== false &&
              (!validTodayAttendance?.clock_in || validTodayAttendance?.clock_out) && (
              <button
                onClick={handleClockIn}
                disabled={isLoading}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#2ea44f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#2c974b')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#2ea44f')}
              >
                {isLoading ? '処理中...' : '出勤打刻'}
              </button>
            )}

            {/* 出勤済み・未退勤: 退勤ボタン・休憩ボタン表示 */}
            {companySettings?.self_punch_enabled !== false &&
              validTodayAttendance?.clock_in && !validTodayAttendance?.clock_out && (
              <>
                <button
                  onClick={handleClockOut}
                  disabled={isLoading}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: '#cf222e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#a40e26')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#cf222e')}
                >
                  {isLoading ? '処理中...' : '退勤打刻'}
                </button>
                {/* 休憩ボタン（自動控除が有効な場合はグレーアウト） */}
                {companySettings?.auto_break_deduction_enabled ? (
                  <div style={{
                    padding: '12px 28px',
                    backgroundColor: '#f6f8fa',
                    color: '#57606a',
                    border: '1px solid #d0d7de',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>休憩時間自動控除</span>
                  </div>
                ) : (
                  <>
                    {!validTodayAttendance?.break_start && (
                      <button
                        onClick={handleBreakStart}
                        disabled={isLoading}
                        style={{
                          padding: '12px 32px',
                          backgroundColor: '#0969da',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0860ca')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0969da')}
                      >
                        {isLoading ? '処理中...' : '休憩開始'}
                      </button>
                    )}
                    {validTodayAttendance?.break_start && !validTodayAttendance?.break_end && (
                      <button
                        onClick={handleBreakEnd}
                        disabled={isLoading}
                        style={{
                          padding: '12px 32px',
                          backgroundColor: '#0969da',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#0860ca')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0969da')}
                      >
                        {isLoading ? '処理中...' : '休憩終了'}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>

        </div>

        {/* 月次統計 */}
        {monthlyStats && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#24292e', fontWeight: '600' }}>
              今月の勤怠統計 ({monthlyStats.month})
            </h2>

            {/* 基本統計 - シンプルなリスト形式 */}
            <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <span style={{ fontSize: '14px', color: '#495057' }}>出勤日数</span>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.completedDays} <span style={{ fontSize: '14px', fontWeight: '400' }}>日</span></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                <span style={{ fontSize: '14px', color: '#495057' }}>休日出勤</span>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.holidayWorkDays} <span style={{ fontSize: '14px', fontWeight: '400' }}>日</span></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                <span style={{ fontSize: '14px', color: '#495057' }}>総勤務時間</span>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.totalHours} <span style={{ fontSize: '14px', fontWeight: '400' }}>時間</span></span>
              </div>
            </div>

            {/* 労働時間詳細 - シンプルなリスト形式 */}
            <div style={{
              borderTop: '1px solid #d0d7de',
              paddingTop: '20px',
              marginTop: '12px'
            }}>
              <h3 style={{ fontSize: '16px', color: '#24292e', marginBottom: '16px', marginTop: 0, fontWeight: '600' }}>労働時間の内訳</h3>
              <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                  <span style={{ fontSize: '14px', color: '#495057' }}>所定内労働</span>
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.totalRegularHours} <span style={{ fontSize: '14px', fontWeight: '400' }}>時間</span></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                  <span style={{ fontSize: '14px', color: '#495057' }}>時間外労働</span>
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.totalOvertimeHours} <span style={{ fontSize: '14px', fontWeight: '400' }}>時間</span></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #dee2e6' }}>
                  <span style={{ fontSize: '14px', color: '#495057' }}>休日労働</span>
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.totalHolidayWorkHours} <span style={{ fontSize: '14px', fontWeight: '400' }}>時間</span></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                  <span style={{ fontSize: '14px', color: '#495057' }}>休日時間外</span>
                  <span style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>{monthlyStats.totalHolidayOvertimeHours} <span style={{ fontSize: '14px', fontWeight: '400' }}>時間</span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 休暇残数 */}
        {leaveBalances.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#24292e', fontWeight: '600' }}>
              休暇残数
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {leaveBalances.map((balance) => (
                <div key={balance.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid #e1e4e8'
                }}>
                  <div style={{ flex: 1, fontSize: '14px', color: '#24292e' }}>
                    {balance.leave_type_name}
                    {balance.is_paid && <span style={{ marginLeft: '6px', fontSize: '11px', backgroundColor: '#ddf4ff', color: '#0969da', padding: '2px 8px', borderRadius: '12px' }}>有給</span>}
                    <div style={{ fontSize: '12px', color: '#6a737d', marginTop: '4px' }}>
                      有効期限: {new Date(balance.valid_until).toLocaleDateString('ja-JP')}
                    </div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#24292e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {balance.balance_days} 日
                    {balance.balance_remaining_hours > 0 && ` ${balance.balance_remaining_hours} 時間`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* チーム出勤状況（閲覧範囲が自分のみ以外） */}
        {showTeamStatus && teamStatus.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#24292e', fontWeight: '600' }}>
              本日のチーム出勤状況
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' }}>
              <div style={{ padding: '15px 20px', backgroundColor: '#fff3cd', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#856404', marginBottom: '5px' }}>勤務中</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>{attendedCount}人</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#d4edda', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#155724', marginBottom: '5px' }}>退勤済み</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#155724' }}>{clockedOutCount}人</div>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: '#f8d7da', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: '#721c24', marginBottom: '5px' }}>未出勤</div>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#721c24' }}>{notAttendedCount}人</div>
              </div>
            </div>
            <div style={{ maxHeight: '400px', overflowX: 'auto', overflowY: 'auto' }}>
              <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f6f8fa', borderBottom: '1px solid #d0d7de' }}>
                    <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#57606a', fontWeight: '600' }}>名前</th>
                    <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#57606a', fontWeight: '600' }}>役職</th>
                    <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#57606a', fontWeight: '600' }}>出勤時刻</th>
                    <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#57606a', fontWeight: '600' }}>退勤時刻</th>
                    <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#57606a', fontWeight: '600' }}>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTeamStatus.map(member => (
                    <tr key={member.id} style={{ borderBottom: '1px solid #e1e4e8' }}>
                      <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#24292e' }}>{member.name}</td>
                      <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#57606a' }}>{member.position_name || '-'}</td>
                      <td style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#24292e' }}>{formatTime(member.clock_in)}</td>
                      <td style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center', fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#24292e' }}>{formatTime(member.clock_out, member.clock_in)}</td>
                      <td style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'center' }}>
                        {!member.clock_in ? (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>未出勤</span>
                        ) : !member.clock_out ? (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#fff3cd',
                            color: '#856404',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>勤務中</span>
                        ) : (
                          <span style={{
                            padding: '4px 12px',
                            backgroundColor: '#d4edda',
                            color: '#155724',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>退勤済み</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ページネーション */}
            {teamStatus.length > teamStatusItemsPerPage && (
              <div style={{ marginTop: '20px' }}>
                <Pagination
                  currentPage={teamStatusPage}
                  totalPages={teamStatusTotalPages}
                  onPageChange={setTeamStatusPage}
                  itemsPerPage={teamStatusItemsPerPage}
                  totalItems={teamStatus.length}
                />
              </div>
            )}
          </div>
        )}

        {/* メニュー */}
        <div>
          <h2 style={{ marginBottom: '20px', fontSize: '20px', color: '#24292e', fontWeight: '600' }}>メニュー</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {/* 1. 勤怠履歴 */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #d0d7de',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'all 0.15s ease',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/attendance-history')}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            >
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>勤怠履歴</h3>
              <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>過去の勤怠記録を確認</p>
            </div>

            {/* 2. 打刻修正申請 */}
            {canApprove && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #d0d7de',
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'left',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'all 0.15s ease',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                console.log('打刻修正申請カードクリック');
                navigate('/correction-requests');
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>打刻修正申請</h3>
                <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>修正申請の承認・却下</p>
              </div>
            )}

            {/* 3. 休暇申請 */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #d0d7de',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'left',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'all 0.15s ease',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/leave-requests')}
            onMouseOver={(e) => {
              e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            >
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>休暇申請</h3>
              <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>有給・休暇の申請と承認</p>
            </div>

            {/* 4. 勤怠データ出力 */}
            {permissions?.canExportAttendance && (
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #d0d7de',
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'left',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'all 0.15s ease',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/attendance-export')}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>勤怠データ出力</h3>
                <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>CSV・PDF形式で出力</p>
              </div>
            )}

            {/* 5-11. マスターのみのメニュー */}
            {isMaster && (
              <>
                {/* 共用端末（キオスク）。店に置いた1台を全員で使う打刻の設定 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/kiosk-devices')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>打刻用共用端末</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>端末の登録と打刻用QRの発行</p>
                </div>

                {/* 5. ユーザー管理 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/users')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>ユーザー管理</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>システムユーザーの管理</p>
                </div>

                {/* 6. 組織管理 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/organization')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>組織管理</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>部署・役職の管理</p>
                </div>

                {/* 7. 休暇残数管理 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/leave-balances')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>休暇残数管理</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>ユーザーの休暇残数付与</p>
                </div>

                {/* 8. 休暇種別設定 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/leave-types')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>休暇種別設定</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>休暇種別の管理</p>
                </div>

                {/* 9. 企業設定 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/company-settings')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>企業設定</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>休憩時間・勤務時間設定</p>
                </div>

                {/* 10. アカウント設定 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/profile')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>アカウント設定</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>パスワード・メールアドレス変更</p>
                </div>

                {/* 11. サブスクリプション管理 */}
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #d0d7de',
                  borderRadius: '8px',
                  padding: '24px',
                  textAlign: 'left',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/subscription')}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#24292e', fontWeight: '600' }}>サブスクリプション管理</h3>
                  <p style={{ margin: '0', color: '#57606a', fontSize: '14px' }}>プラン・支払い情報</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
